// @ts-nocheck
import { useEffect, useState, ReactNode } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { Avatar } from "@mui/material";
import { rolePermissionExists } from "utils/CommonUtils";
import MDSnackbar from "components/MDSnackbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import SidenavCollapse from "lib/Sidenav/SidenavCollapse";
import SidenavList from "lib/Sidenav/SidenavList";
import SidenavItem from "lib/Sidenav/SidenavItem";

import SidenavRoot from "lib/Sidenav/SidenavRoot";
import sidenavLogoLabel from "lib/Sidenav/styles/sidenav";

import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

import ProfileCard from "lib/Sidenav/ProfileCard";
import { get, post } from "requests";
import StorageUtils from "utils/StorageUtils";
import CustomDialog from "components/CustomCard";

interface Props {
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  brand?: string;
  brandName: string;
  routes: {
    [key: string]:
      | ReactNode
      | string
      | {
          [key: string]:
            | ReactNode
            | string
            | {
                [key: string]: ReactNode | string;
              }[];
        }[];
  }[];
  [key: string]: any;
}

function Sidenav({
  color,
  brand,
  brandName,
  routes,
  alert,
  setAlert,
  ...rest
}: Props): JSX.Element {
  const [openCollapse, setOpenCollapse] = useState<boolean | string>(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState<boolean | string>(false);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [tenantLogoPath, setTenantLogoPath] = useState("");
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname?.split("/").slice(1)[0];
  const items = pathname?.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];
  const userId = localStorage.getItem("logInUserId");
  const [dialog, setDialog] = useState<any>(null);

  const capitalize = (str) => {
    if (str) return str.charAt(0).toUpperCase() + str.slice(1);
    else return "";
  };

  const capitalizeAndSetName = (name) => {
    setFullName(capitalize(name));
  };

  const [fullName, setFullName] = useState(capitalize(localStorage.getItem("fullName")));

  const [collapseOpen, setCollapseOpen] = useState(false);

  const getInitials = (name) => {
    if (!name) return "";
    const names = name?.trim()?.split(" ");
    return (
      (names[0]?.[0] || "") + (names.length > 1 ? names[names.length - 1]?.[0] || "" : "")
    ).toUpperCase();
  };

  const getRandomColor = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 80%, 40%)`;
  };

  let textColor:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "dark"
    | "white"
    | "inherit"
    | "text"
    | "light" = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  const handleCollapseToggle = () => {
    setCollapseOpen(!collapseOpen);
  };

  const hasPermission = (permission: { action: string; resource: string }): boolean => {
    if (!permission) return true;

    try {
      return rolePermissionExists(
        window.rolePermission?.permissions || [],
        permission.action,
        permission.resource
      );
    } catch (e) {
      console.error("Permission check failed:", e);
      return false;
    }
  };

  const isFeatureEnabled = (feature) => {
    if (!feature) return true;
    return StorageUtils.isFeatureEnabled(feature);
  };

  const isRouteAllowed = (route: any): boolean => {
    const { permission, feature, additionalCheck } = route;

    if ((permission && !hasPermission(permission)) || (feature && !isFeatureEnabled(feature))) {
      return false;
    }

    if (typeof additionalCheck === "function" && !additionalCheck()) {
      return false;
    }

    if (route.collapse) {
      route.collapse = route.collapse.filter(isRouteAllowed);
      return route.collapse.length > 0;
    }

    return true;
  };

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
    fetchTenantConfiguration();
  }, []);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    window.addEventListener("resize", handleMiniSidenav);

    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const fetchTenantConfiguration = async () => {
    let tenantId = localStorage.getItem("tenantId");
    await get(`getConfiguration/${tenantId}`).then((responseData) => {
      if (responseData.profilePath) {
        let formattedPath = "https://s3.amazonaws.com/" + responseData.profilePath;
        setTenantLogoPath(formattedPath);
      }
    });
  };

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse: any) => {
    const template = collapse.map(({ name, route, key, href }: any) =>
      href ? (
        <Link key={key} href={href} rel="noreferrer" sx={{ textDecoration: "none" }}>
          <SidenavItem name={name} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
          <SidenavItem name={name} active={route === pathname} nested />
        </NavLink>
      )
    );

    return template;
  };

  const renderCollapse = (collapses: any) =>
    collapses.map(({ name, collapse, route, href, key }: any) => {
      let returnValue;

      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            active={key === itemParentName ? "isParent" : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }: any) =>
              openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link href={href} key={key} rel="noreferrer" sx={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={name} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={name} active={key === itemName} />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  const renderRoutes = routes
    .filter((route: any) => isRouteAllowed(route))
    .map(({ type, name, icon, title, collapse, noCollapse, key, href, route, component }: any) => {
      let returnValue;

      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      } else if (type === "single") {
        returnValue = (
          <NavLink
            to={route}
            key={key}
            style={{ textDecoration: "none" }}
            onClick={key === "current-tenant" ? handleOpen : null}
          >
            <SidenavCollapse
              isProfile={key === "current-tenant"}
              name={key === "current-tenant" ? fullName : name}
              style={{ paddingLeft: key === "current-tenant" ? "25px" : "8px" }}
              icon={
                key === "current-tenant" ? (
                  <Avatar
                    sx={{
                      width: 50,
                      height: 50,
                      position: "absolute",
                      top: "60%",
                      left: "25px",
                      transform: "translateY(-50%)",
                      border: "2px solid white",
                      backgroundColor: getRandomColor(
                        (localStorage.getItem("fullName") || "").split(" ")[0]
                      ),
                      color: "white",
                      fontSize: 24,
                      fontWeight: 500,
                    }}
                  >
                    {getInitials(fullName)}
                  </Avatar>
                ) : (
                  icon
                )
              }
              active={route === pathname}
              noCollapse={true}
            />
          </NavLink>
        );
      } else if (type === "legacy") {
        returnValue = (
          <Link href={route} key={key} style={{ textDecoration: "none" }}>
            <SidenavCollapse
              style={{ paddingLeft: "8px" }}
              name={name}
              icon={icon}
              active={route === pathname}
              noCollapse={true}
            />
          </Link>
        );
      }

      return returnValue;
    });

  // const handleTenantChange = async (tenant: string) => {
  //   const result = await post("/changeTenant", { tenant: tenant });
  //   if (result) {
  //     localStorage.setItem("currentTenant", tenant);
  //     localStorage.setItem("tenantName", tenant);
  //
  //     // Get the user's role from local storage
  //     const role = localStorage.getItem("roleName") ? localStorage.getItem("roleName").toLowerCase() : '';
  //     let destination = '';
  //
  //     // 1. Decide the destination based on the role
  //     if (role.includes('syndicator')) {
  //       // If the user is a syndicator, this is their default page
  //       destination = '/#/syndicatorsdirectdashboard';
  //     } else {
  //       // For all other roles, this is their default page
  //       destination = '/#/dashboard';
  //     }
  //
  //     // This handles the special case for deal applications
  //     const isInAppRoute = window.location.hash.includes("deal_applications/");
  //     if (isInAppRoute) {
  //       destination = '/#/deal_applications';
  //     }
  //
  //     // 2. Execute the redirect to the correct destination
  //     window.location.replace(
  //       `${window.location.origin}${window.location.pathname}${destination}`
  //     );
  //     window.location.reload();
  //   }
  // };

  const handleTenantChange = async (tenant: string) => {
    const result = await post("/changeTenant", { tenant: tenant });
    if (result) {
      localStorage.setItem("currentTenant", tenant);
      localStorage.setItem("tenantName", tenant);

      const isInAppRoute = window.location.hash.includes("deal_applications/");

      if (isInAppRoute) {
        window.location.replace(
          `${window.location.origin}${window.location.pathname}#/deal_applications`
        );
        window.location.reload();
      } else {
        window.location.replace(`${window.location.origin}${window.location.pathname}#/dashboard`);
        window.location.reload();
      }
    }
  };

  const tenantsList = localStorage.getItem("tenantsList");
  const tenants = (tenantsList || "")?.split(",").map((item) => ({
    [item]: item.replace(/_/g, " ").toUpperCase(),
  }));

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={1} textAlign="center" style={{ background: "white" }}>
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && "100%"}
            padding={"auto"}
            sx={(theme: any) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <ListItem button onClick={handleCollapseToggle} sx={{ p: 0 }}>
              <div className={"d-flex d-space-betn d-item-center"}>
                {tenantLogoPath ? (
                  <img
                    src={tenantLogoPath}
                    style={{ height: "40px", width: "200px", marginRight: "3px" }}
                  />
                ) : (
                  <div>{brandName.replaceAll("_", " ")?.toUpperCase()}</div>
                )}
              </div>
              <Icon sx={{ ml: 1 }}>{collapseOpen ? "expand_less" : "expand_more"}</Icon>
            </ListItem>
          </MDBox>
        </MDBox>
      </MDBox>
      <List px={3}>
        <Collapse className={"tenant-collapse"} in={collapseOpen} timeout="auto" unmountOnExit>
          <List>
            {tenants.map((tnt, i) => {
              const tenant = Object.keys(tnt)[0];
              return (
                <div className="nav-tenant-item" key={i} onClick={() => handleTenantChange(tenant)}>
                  {tnt[tenant]}
                </div>
              );
            })}
          </List>
        </Collapse>
        <Divider
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
        {renderRoutes}

        <ProfileCard
          userId={userId}
          tenantName={brandName}
          fullName={fullName}
          setFullName={capitalizeAndSetName}
          open={open}
          onClose={handleClose}
          alert={alert}
          setAlert={setAlert}
          setDialog={setDialog}
        />

        <CustomDialog
          open={!!dialog}
          onClose={() => setDialog(null)}
          title={dialog}
          confirmText="Yes, Proceed"
          cancelText="No, I'll do later"
          onConfirm={() => window.location.reload()}
        />

        <SidenavCollapse
          name={"Logout"}
          icon={<Icon fontSize="medium">power_settings_new</Icon>}
          noCollapse={true}
          onClick={() => {
            window.App._handleLogout();
          }}
        />
      </List>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

export default Sidenav;
